/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { useLogin } from '@utils/api/users/use-login.tsx';
import { useLoginMfa } from '@utils/api/users/use-login-mfa.tsx';
import {
  API_BASE_URL,
  REGISTRATION_ENABLED
} from '@utils/constants/app-constants.ts';
import { PATHS } from '../router/paths.ts';

import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import PublicLogo from '@components/shared/public-logo.tsx';
import { TermsAndConditions } from '@components/shared/terms-and-conditions.tsx';

import { EyeOff, EyeOn, MicrosoftIcon } from '@components/shared/icons.tsx';
import { UploadCloud, Wand2, DownloadCloud } from 'lucide-react';

import toast from 'react-hot-toast';
import scriptixDescImg from '@assets/images/scriptixDescImg.png';
import type { LoginRequest } from '@utils/types/profile/user-profile.ts';

export default function LoginImageFirst() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [openTermsAndConditions, setOpenTermsAndConditions] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme');
      const theme = stored?.replace(/"/g, '');

      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else if (theme === 'light') {
        document.documentElement.classList.remove('dark');
      }
    } catch {
      // ignore if localStorage unavailable
    }
  }, []);

  const {
    mutate: login,
    isLoading: isLoadingLogin,
    isError: isErrorLogin,
    uiError: loginUiError,
    mfaRequired
  } = useLogin();

  // Check if error is related to password expiration
  const isPasswordExpired =
    loginUiError &&
    (loginUiError.toLowerCase().includes('expir') ||
      loginUiError.toLowerCase().includes('reset'));

  const {
    mutate: loginMfa,
    isLoading: isLoadingMfa,
    isError: isErrorMfa,
    uiError: mfaUiError
  } = useLoginMfa();

  const methods = useForm<LoginRequest>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      rememberMe: localStorage.getItem('scriptix_remember_me') === 'true'
    },
    resolver: yupResolver<LoginRequest>(
      Yup.object({
        username: Yup.string()
          .email(t('enter-a-valid-email'))
          .required(t('email-required')),
        password: Yup.string()
          .required(t('password-is-required'))
          .min(8, t('password-must-be-at-least-8-characters-long'))
          .matches(
            /[A-Z]/,
            t('password-must-have-at-least-one-uppercase-letter')
          )
          .matches(
            /[a-z]/,
            t('password-must-have-at-least-one-lowercase-letter')
          )
          .matches(/\d/, t('password-must-have-at-least-one-number')),
        rememberMe: Yup.boolean().optional(),
        mfaCode: Yup.string().optional()
      })
    )
  });

  const { errors, isValid } = methods.formState;

  useEffect(() => {
    if (mfaRequired) document.getElementById('mfaCode')?.focus();
  }, [mfaRequired]);

  const onSubmit = (credentials: LoginRequest) => {
    // Extra safety: check for validation errors before submitting
    if (errors.username || errors.password) {
      if (errors.username) {
        toast.error(errors.username.message || t('enter-a-valid-email'));
      }
      if (errors.password) {
        toast.error(errors.password.message || t('password-is-required'));
      }
      return;
    }

    if (mfaRequired) {
      if (!credentials.mfaCode) return toast.error(t('2fa-code-required'));
      loginMfa(credentials);
    } else {
      login(credentials);
    }
  };

  const handleLoginMicrosoft = () => {
    const { protocol, host } = window.location;
    const redirectUri = `${protocol}//${host}/auth-callback/microsoft`;
    window.location.href = `${API_BASE_URL}/api/v3/auth/login/microsoft?redirect_uri=${redirectUri}`;
  };

  const steps = [
    {
      title: 'Upload your audio or video files',
      desc: 'Upload from your device; we support popular formats for smooth imports today too.',
      Icon: UploadCloud
    },
    {
      title: 'Review and edit with our tools',
      desc: 'Use our transcript editor to edit, translate, and collaborate with your team easily online.',
      Icon: Wand2
    },
    {
      title: 'Download your finished files',
      desc: 'Download transcripts or subtitles as DOCX, SRT, TTML, VTT, or SBV files.',
      Icon: DownloadCloud
    }
  ];

  const clampWords = (text: string, maxWords: number) => {
    if (!text) return text;
    const parts = text.trim().split(/\s+/);
    return parts.length <= maxWords
      ? text
      : parts.slice(0, maxWords).join(' ') + '…';
  };

  return (
    <div
      className="
        bg-app relative isolate
        min-h-[100svh] md:h-[100svh] md:max-h-[100svh]
        overflow-auto
        text-zinc-900
        dark:text-zinc-50
        pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]
      "
    >
      {/* Background glow */}
      <div
        aria-hidden
        className="
          pointer-events-none absolute inset-0 -z-10
          bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.16),transparent_55%)]
          dark:bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.35),transparent_55%)]
        "
      />

      <div
        className="
          mx-auto h-full w-full
          px-3 sm:px-4 md:px-0
          flex flex-col
          md:grid
          md:[grid-template-columns:30%_70%]
          rounded-2xl border border-zinc-200/70 bg-white/85
          shadow-xl shadow-zinc-900/8
          backdrop-blur-xl
          dark:border-zinc-800 dark:bg-zinc-950/92 dark:shadow-[0_24px_80px_rgba(0,0,0,0.75)]
        "
      >
        {/* LEFT SIDE: auth */}
        <div className="flex h-full min-h-0">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="
              relative w-full h-full min-h-0
              flex flex-col
              border-t md:border-t-0 border-zinc-400/50
              bg-zinc-100
              dark:border-zinc-800 dark:bg-zinc-950
            "
          >
            {/* MIDDLE SECTION: vertically centered content */}
            <div
              className="
                flex-1 min-h-0 px-5 sm:px-7 py-4 pt-16 md:pt-0
                flex items-center justify-center
              "
            >
              <div className="w-full max-w-md flex flex-col gap-4">
                {/* Logo */}
                <div className="flex items-center justify-center">
                  <PublicLogo className="hidden md:block w-64 h-auto" />
                  <PublicLogo className="block md:hidden w-40 h-auto" />
                </div>

                {/* Header text (outside card) */}
                <div className="text-center">
                  <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                    {t('sign-in-to-your-account')}
                  </h1>
                </div>

                {/* Sign-up hint (outside card) */}
                {REGISTRATION_ENABLED && !mfaRequired && (
                  <div className="text-center">
                    <p className="text-sm text-zinc-700 dark:text-zinc-300">
                      {t('dont-have-account')}{' '}
                      <button
                        type="button"
                        className="font-semibold text-primary underline-offset-2 hover:underline cursor-pointer"
                        onClick={() => navigate(PATHS().REGISTER)}
                      >
                        {t('sign-up-for-free')}
                      </button>
                    </p>
                  </div>
                )}

                {/* CARD: ONLY the form + error + SSO */}
                <div
                  className="
                    rounded-2xl border border-zinc-400/50 bg-white
                    shadow-sm shadow-zinc-900/5
                    dark:border-zinc-700/80 dark:bg-zinc-900
                    dark:shadow-[0_18px_45px_rgba(0,0,0,0.55)]
                    px-6 py-8
                  "
                >
                  <form
                    className="w-full flex flex-col gap-4"
                    onSubmit={methods.handleSubmit(onSubmit)}
                  >
                    {!mfaRequired && (
                      <div>
                        <label
                          htmlFor="username"
                          className="mb-1 block text-sm font-medium text-zinc-800 dark:text-zinc-100"
                        >
                          {t('email-address')}
                        </label>
                        <Input
                          {...methods.register('username')}
                          id="username"
                          type="email"
                          autoComplete="email"
                          className="w-full"
                          error={errors.username?.message}
                        />
                      </div>
                    )}

                    {!mfaRequired && (
                      <div>
                        <label
                          htmlFor="password"
                          className="mb-1 block text-sm font-medium text-zinc-800 dark:text-zinc-100"
                        >
                          {t('password')}
                        </label>
                        <div className="relative">
                          <Input
                            {...methods.register('password')}
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            className="w-full pr-10"
                          />
                          <button
                            type="button"
                            aria-label={
                              showPassword
                                ? t('hide-password')
                                : t('show-password')
                            }
                            className="
                              absolute inset-y-0 right-2 my-auto inline-flex h-8 w-8
                              items-center justify-center rounded-2xl
                              text-zinc-500 hover:bg-zinc-100
                              focus:outline-none focus:ring-2 focus:ring-primary
                              dark:text-zinc-400 dark:hover:bg-zinc-800
                            "
                            onClick={() => setShowPassword(p => !p)}
                          >
                            {showPassword ? (
                              <EyeOn className="h-5 w-5" />
                            ) : (
                              <EyeOff className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.password.message}
                          </p>
                        )}
                      </div>
                    )}

                    {mfaRequired && (
                      <div>
                        <label
                          htmlFor="mfaCode"
                          className="mb-1 block text-sm font-medium text-zinc-800 dark:text-zinc-100"
                        >
                          {t('2fa-code')}
                        </label>
                        <input
                          {...methods.register('mfaCode')}
                          id="mfaCode"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          autoComplete="one-time-code"
                          required
                          placeholder="000000"
                          className="
                            block w-full rounded-xl border border-zinc-300/80 bg-white/60
                            p-2.5 text-sm text-zinc-900 shadow-sm outline-none
                            placeholder:text-zinc-400
                            focus:border-primary focus:ring-2 focus:ring-primary/30
                            dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100
                          "
                        />
                        {errors.mfaCode && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.mfaCode.message}
                          </p>
                        )}
                      </div>
                    )}

                    {!mfaRequired && (
                      <div className="flex items-center justify-between gap-3">
                        <Checkbox
                          id="remember_me"
                          label={t('remember-me')}
                          variant="default"
                          register={methods.register('rememberMe')}
                        />
                        <button
                          type="button"
                          className="text-sm font-medium text-primary hover:underline underline-offset-2"
                          onClick={() => navigate('/reset-password')}
                        >
                          {t('forgot-password')}?
                        </button>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={
                        isLoadingLogin ||
                        isLoadingMfa ||
                        (!mfaRequired && !isValid)
                      }
                      loading={isLoadingLogin || isLoadingMfa}
                      label={t('login')}
                      className="w-full justify-center"
                    />

                    {(isErrorLogin || isErrorMfa) && (
                      <div className="text-center space-y-2">
                        <p className="text-sm font-semibold text-red-500">
                          {loginUiError || mfaUiError}
                        </p>
                        {isPasswordExpired && (
                          <Button
                            type="button"
                            variant="outline"
                            size="small"
                            label={t('password-reset')}
                            onClick={() => navigate('/reset-password')}
                            className="w-full justify-center"
                          />
                        )}
                      </div>
                    )}

                    {!mfaRequired && (
                      <>
                        <div className="my-1.5 flex items-center gap-3">
                          <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-300/80 to-transparent dark:via-zinc-700" />
                          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                            {t('or-continue-with')}
                          </span>
                          <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-300/80 to-transparent dark:via-zinc-700" />
                        </div>
                        <Button
                          label="Microsoft"
                          icon={<MicrosoftIcon />}
                          variant="outline"
                          onClick={handleLoginMicrosoft}
                          className="w-full justify-center"
                        />
                      </>
                    )}
                  </form>
                </div>

                {/* Help Section */}
                <div className="pt-3 border-t border-zinc-200/70 dark:border-zinc-700/50 mt-2">
                  <div className="flex items-center justify-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-300">
                    <span>{t('need-help')}</span>
                    <a
                      href="mailto:info@scriptix.io"
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline underline-offset-2 transition-colors"
                    >
                      {t('contact-support')}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms strip pinned at bottom of LEFT side */}
            <div className="border-t border-zinc-400/50 px-5 sm:px-7 py-3 text-center text-[0.78rem] text-zinc-600 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400">
              {t('by-logging-in-to-our-site-you-have-read-and-agree-to-our')}{' '}
              <button
                className="text-primary underline-offset-2 hover:underline cursor-pointer"
                onClick={() => setOpenTermsAndConditions(true)}
              >
                {t('terms-and-conditions')}
              </button>
            </div>
          </motion.div>
        </div>

        {/* RIGHT SIDE: hero + image + steps (hidden on small screens) */}
        <div className="hidden md:flex h-full min-h-0 bg-zinc-100 dark:bg-zinc-900">
          <div
            className="
              grid h-full min-h-0 w-full
              grid-rows-[0.16fr_0.63fr_0.19fr]
              gap-y-3 md:gap-y-4
              px-4 md:px-6 py-4
            "
          >
            {/* Row 1: hero text */}
            <div className="flex flex-col justify-center items-center text-center py-4">
              <h2
                className="
                  text-[1.75rem] md:text-[2.5rem] leading-tight
                  font-semibold
                  text-zinc-800 dark:text-zinc-100
                  tracking-tight
                "
              >
                {t('making-media-accessible-to')}{' '}
                <span className="text-[#2584f5]">{t('everyone')}</span>
              </h2>

              <p
                className="
                  mt-3
                  text-sm md:text-base
                  leading-relaxed
                  text-zinc-600 dark:text-zinc-400
                  max-w-xl
                "
              >
                {t(
                  'hero-subtag_v2',
                  'Fast, accurate transcripts and subtitles. Upload your media, make quick edits, and export in the formats you need.'
                )}
              </p>
            </div>

            {/* Row 2: large image */}
            <div className="relative flex-shrink-0 overflow-hidden rounded-2xl border border-zinc-200/80 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="relative w-full h-full">
                <img
                  src={scriptixDescImg}
                  alt={t('review-and-edit')}
                  className="absolute inset-0 h-full w-full object-cover object-center opacity-85"
                />

                {/* Top blend */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-white/92 via-white/40 to-transparent dark:from-slate-950/96 dark:via-slate-950/70 dark:to-transparent" />

                {/* Right blend */}
                <div className="pointer-events-none absolute inset-y-0 right-0 w-44 bg-gradient-to-l from-white/92 via-white/40 to-transparent dark:from-slate-950/96 dark:via-slate-950/70 dark:to-transparent" />

                {/* Bottom blend */}
                <div className="pointer-events-none absolute inset-x-0 -bottom-2 h-48 bg-gradient-to-t from-white/92 via-white/40 to-transparent dark:from-slate-950/96 dark:via-slate-950/70 dark:to-transparent" />
              </div>

              {/* Bottom label */}
              <div className="absolute inset-x-4 bottom-3">
                <p className="inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1 text-[11px] font-medium text-zinc-100 backdrop-blur-sm dark:bg-black/75">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {t(
                    'hero-subtag_v2',
                    'Fast, accurate transcripts and subtitles.'
                  )}
                </p>
              </div>
            </div>

            {/* Row 3: steps */}
            <div className="min-h-0 h-full">
              {/* md+ : nice static grid (2 cols on md, 3 on lg) */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-2.5 h-full">
                {steps.map(({ title, desc, Icon }, i) => (
                  <div
                    key={i}
                    className="
          group relative flex h-full flex-col
          rounded-xl border border-zinc-400/50 bg-white/92
          p-3 shadow-sm overflow-hidden
          dark:border-zinc-800 dark:bg-zinc-900/90 dark:shadow-[0_18px_40px_rgba(0,0,0,0.5)]
        "
                  >
                    <div
                      aria-hidden
                      className="
            pointer-events-none absolute inset-[-1px]
            rounded-[inherit] opacity-0
            bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.35),transparent_55%)]
            transition-opacity duration-300
            group-hover:opacity-100
          "
                    />

                    <div className="relative flex h-full flex-col items-center justify-center text-center gap-2">
                      <span className="inline-flex items-center justify-center rounded-2xl bg-zinc-100 p-2.5 ring-1 ring-zinc-200/80 dark:bg-zinc-900 dark:ring-zinc-700/80">
                        <Icon className="h-5 w-5 text-[#2584f5]" aria-hidden />
                      </span>

                      <span className="text-[0.7rem] uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400">
                        {t('step')} {i + 1}
                      </span>

                      <h3
                        className="
              text-[0.9rem] font-semibold leading-snug text-zinc-900 dark:text-zinc-50
              [display:-webkit-box] [WebkitLineClamp:2] [WebkitBoxOrient:vertical]
              overflow-hidden
            "
                      >
                        {clampWords(title, 10)}
                      </h3>

                      <p
                        className="
              text-[0.8rem] leading-snug text-zinc-600 dark:text-zinc-300
              [display:-webkit-box] [WebkitLineClamp:3] [WebkitBoxOrient:vertical]
              overflow-hidden
            "
                      >
                        {clampWords(desc, 22)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Optional: keep mobile horizontal cards for <md (won't show right now because parent is hidden md:flex,
      but safe if you ever show the right side on phones again) */}
              <div className="md:hidden">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 pt-1 [-webkit-overflow-scrolling:touch]">
                  {steps.map(({ title, desc, Icon }, i) => (
                    <div
                      key={i}
                      className="
            snap-start min-w-[75%] max-w-[75%] sm:min-w-[60%] sm:max-w-[60%]
            rounded-xl border border-zinc-200/80 bg-white/95 p-3
            shadow-sm flex flex-col gap-1.5
            text-xs
            dark:border-zinc-800 dark:bg-zinc-900/90
          "
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center rounded-2xl bg-zinc-100 p-2 ring-1 ring-zinc-200/80 dark:bg-zinc-900 dark:ring-zinc-700/80">
                          <Icon
                            className="h-4 w-4 text-[#2584f5]"
                            aria-hidden
                          />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400">
                          {t('step')} {i + 1}
                        </span>
                      </div>
                      <h3 className="text-[0.8rem] font-semibold leading-snug text-zinc-900 dark:text-zinc-50">
                        {clampWords(title, 9)}
                      </h3>
                      <p className="text-[0.72rem] leading-snug text-zinc-600 dark:text-zinc-300">
                        {clampWords(desc, 22)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openTermsAndConditions && (
        <Modal
          isOpen={openTermsAndConditions}
          onClose={() => setOpenTermsAndConditions(false)}
          size="x-large"
          footerActions={[
            <Button
              key="close"
              onClick={() => setOpenTermsAndConditions(false)}
              label={t('close')}
              size="small"
              variant="secondary"
            />
          ]}
        >
          <TermsAndConditions />
        </Modal>
      )}
    </div>
  );
}
